// @subframe/sync-disable
"use client";
/*
 * Documentation:
 * Table — https://app.subframe.com/06b570ce924b/library?component=Table_142dfde7-d0cc-48a1-a04c-a08ab2252633
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children?: React.ReactNode;
  clickable?: boolean;
  className?: string;
}

const Row = React.forwardRef<HTMLTableRowElement, RowProps>(function Row(
  { children, clickable = false, className, ...otherProps }: RowProps,
  ref,
) {
  return (
    <tr
      className={SubframeUtils.twClassNames(
        "group/5d119f8d border-t border-solid border-secondary-beige-200 rounded-sm",
        { "hover:bg-neutral-50": clickable },
        className,
      )}
      ref={ref}
      {...otherProps}
    >
      {children}
    </tr>
  );
});

interface CellProps extends React.TdHTMLAttributes<HTMLTableDataCellElement> {
  children?: React.ReactNode;
  variant?: "aligned-left" | "aligned-right";
  className?: string;
}

const Cell = React.forwardRef<HTMLTableCellElement, CellProps>(function Cell(
  { children, variant = "aligned-left", className, ...otherProps }: CellProps,
  ref,
) {
  return (
    // className lands on the <td>, not the inner div: a table cell treats `height`
    // as a *minimum* (CSS 2.2 §17.5.3), so a consumer's `h-14` can never clip
    // wrapped content, and `w-*`/`min-w-*` act as real column constraints.
    // Use `variant="aligned-right"` for alignment — flex classes have no effect here.
    <td
      className={SubframeUtils.twClassNames("align-middle", className)}
      ref={ref}
      {...otherProps}
    >
      <div
        className={SubframeUtils.twClassNames(
          "group/291d4418 flex min-h-12 w-full items-center gap-1 px-3 py-1.5",
          { "justify-end": variant === "aligned-right" },
        )}
      >
        {children}
      </div>
    </td>
  );
});

interface HeaderRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children?: React.ReactNode;
  className?: string;
}

const HeaderRow = React.forwardRef<HTMLTableRowElement, HeaderRowProps>(
  function HeaderRow(
    { children, className, ...otherProps }: HeaderRowProps,
    ref,
  ) {
    return (
      <tr className={className} ref={ref} {...otherProps}>
        {children}
      </tr>
    );
  },
);

interface HeaderCellProps extends React.ThHTMLAttributes<HTMLTableHeaderCellElement> {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  variant?: "aligned-left" | "aligned-right";
  className?: string;
}

const HeaderCell = React.forwardRef<HTMLDivElement, HeaderCellProps>(
  function HeaderCell(
    {
      children,
      icon = null,
      variant = "aligned-left",
      className,
      ...otherProps
    }: HeaderCellProps,
    ref,
  ) {
    return (
      // Unlike Cell, className stays on the inner div: 12 consumers pass flex
      // classes (`items-center justify-end`) here, which would be inert on a <th>.
      <th {...otherProps}>
        <div
          className={SubframeUtils.twClassNames(
            "group/a3a9d884 flex min-h-8 w-full items-center gap-1 px-3 text-left",
            { "items-center justify-end": variant === "aligned-right" },
            className,
          )}
          ref={ref}
        >
          {children ? (
            <span
              className={SubframeUtils.twClassNames(
                "whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color",
                { "text-right": variant === "aligned-right" },
              )}
            >
              {children}
            </span>
          ) : null}
          {icon ? (
            <SubframeCore.IconWrapper className="text-caption font-caption text-subtext-color">
              {icon}
            </SubframeCore.IconWrapper>
          ) : null}
        </div>
      </th>
    );
  },
);

interface TableRootProps extends React.TableHTMLAttributes<HTMLTableElement> {
  header?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const TableRoot = React.forwardRef<HTMLTableElement, TableRootProps>(
  function TableRoot(
    { header, children, className, ...otherProps }: TableRootProps,
    ref,
  ) {
    return (
      <table
        className={SubframeUtils.twClassNames("w-full", className)}
        ref={ref}
        {...otherProps}
      >
        <thead>{header}</thead>
        <tbody className="border-b border-solid border-secondary-beige-200">
          {children}
        </tbody>
      </table>
    );
  },
);

export const Table = Object.assign(TableRoot, {
  Row,
  Cell,
  HeaderRow,
  HeaderCell,
});
